import { Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

import {
  CommonGenerationTileProps,
  GenerationTileAction,
} from "@/components/Tiles/GenerationTile/types";
import {
  CircularProgress,
  TileLikeButton,
  TileObjectTypeIndicator,
} from "@/components/ui-v2";
import { TileBase } from "@/components/ui-v2/Tiles/TileBase/TileBase";
import { SessionFileFragment } from "@/features/forge/forge-session/queries.graphql";

import { MeshGenerationTileButtons } from "./MeshGenerationTileButtons";
import { getPreviewStatus } from "./utils";

const Root = styled(TileBase, {
  shouldForwardProp: (prop) => prop !== "previewStatus",
})<{ previewStatus: string }>(({ theme, previewStatus }) => ({
  transition: "all 0.1s ease-in-out",
  background:
    "radial-gradient(circle, rgba(35,43,75,1) 30%, rgba(65,66,80,1) 100%) no-repeat center / cover",
  ...(previewStatus !== "success" && {
    background: theme.palette.secondary[7],
  }),
}));

export const MeshGenerationTile = (
  props: CommonGenerationTileProps<SessionFileFragment>,
) => {
  const {
    object,
    isSelected = false,
    isSelectable = false,
    isLiking = false,
    onAction,
    ...rest
  } = props;
  const { previewUrl, isLiked } = object;

  // The preview image is generated server-side during file post-processing and
  // delivered via the session object subscription — no client-side rendering.
  const previewStatus = getPreviewStatus(previewUrl);

  const handleTileBaseAction =
    (action: GenerationTileAction) => (e: React.MouseEvent) => {
      onAction?.(e, action);
    };

  return (
    <Root
      previewStatus={previewStatus}
      isSelected={isSelected}
      isSelectable={isSelectable}
      slots={{
        bottomLeftEl: MeshGenerationTileButtons,
        bottomRightEl: TileLikeButton,
        topLeftEl: TileObjectTypeIndicator,
      }}
      slotProps={{
        bottomLeftEl: {
          onAction,
        },
        bottomRightEl: {
          isLiked,
          isLoading: isLiking,
          onAction,
          sx: {
            m: 1,
          },
        },
        topLeftEl: {
          isSelected,
          objectType: "mesh",
        },
      }}
      onClick={handleTileBaseAction("SELECT")}
      onDoubleClick={handleTileBaseAction("DOUBLE_CLICK")}
      onContextMenu={handleTileBaseAction("OPEN_CONTEXT_MENU")}
      {...rest}
    >
      {previewStatus === "generating" && (
        <Stack sx={{ textAlign: "center" }} spacing={6}>
          <Typography
            variant="title1"
            sx={{
              color: "secondary.2",
              display: "block",
            }}
          >
            <CircularProgress />
          </Typography>
          <Typography
            variant="regular"
            sx={{
              color: "secondary.2",
            }}
          >
            Generating Preview Image...
          </Typography>
        </Stack>
      )}
      {previewStatus === "success" && previewUrl && (
        <img src={previewUrl} alt={object.name} loading="lazy" />
      )}
    </Root>
  );
};
